import { reactive } from 'vue'
import { deepCopy } from '@/util/deep-copy'
import moment from '@/util/MPMoment'
import store from '@/store/index'

/**
 * 근무일정 팝업(WorkStatePopup)의 공유 상태.
 *
 * 월 선택·수정모드·편집 레코드·레이어 표시·복사/붙여넣기 상태를 팝업 내부
 * 컴포넌트들이 공유한다. 근무코드/필터 플래그는 병원별로 vuex 에 영속화되므로
 * 반드시 set* 함수를 통해 변경한다 (직접 대입 시 영속화가 누락된다).
 * 구 WorkerStateDataController 를 1:1 대체한다 (죽은 popup 머신 제외).
 */

type WorkCode = { _id: string; isSelected?: boolean; [key: string]: unknown }
type StoreState = {
    hospital?: { selectedHospital?: { _id?: string } }
    V2WorkCodeFilter: { hospitals: Record<string, { isShowCustomTimeCode?: boolean; isShowBasicEmployee?: boolean }> }
    V2WorkCode: { hospitals: Record<string, WorkCode[]> }
}
const storeState = store.state as unknown as StoreState

function currentHospitalID(): string {
    return storeState.hospital?.selectedHospital?._id ?? ''
}

/** 리셋 시점마다 새로 평가한다 — 모듈 로드 시점 고정(지난달 리셋 버그)과 참조 공유 오염을 막는다 */
function initialState() {
    const hospitalID = currentHospitalID()
    const filter = storeState.V2WorkCodeFilter.hospitals[hospitalID]
    return {
        selectedDate: moment().startOf('month') as unknown,

        lastBatchPushNotification: undefined as unknown,

        keyEventController: undefined as unknown,

        // 병원별 vuex 영속값을 복원한다 (구 컨트롤러 생성자와 동일)
        isShowCustomTimeCode: filter?.isShowCustomTimeCode ?? true,
        isShowBasicEmployee: filter?.isShowBasicEmployee ?? false,
        workCodes: [] as WorkCode[],
        updateBatch: undefined as unknown,

        dateArray: [] as unknown[],

        isModifyMode: false as boolean | undefined,
        focusedRecord: undefined as unknown,
        focusedElement: undefined as unknown,
        f_SelectEmployee: undefined as ((...args: unknown[]) => void) | undefined,
        f_UnselectEmployee: undefined as ((...args: unknown[]) => void) | undefined,

        originRecords: [] as unknown,
        /*
         *  date: {
         *      modifiedRecords: [],
         *      removalRecords: [],
         *      isModified: Boolean
         *  }
         */
        modifiedRecords: {} as Record<string, unknown> | undefined,
        oldWorkCodeRecords: undefined as unknown,
        deleteEmployeesInRecord: false,

        isShowWorkerSetting: false,
        isShowWorkCodeSetting: false,

        scheduleMenu_Date: undefined as unknown,
        searchLayer_Key: undefined as unknown,
        calendarEditLayer_Date: undefined as unknown,
        customTimeOverlay_Date: undefined as unknown,

        needUpdateRecords: false,
        needUpdateCodes: false,
        needUpdateHolydays: false,
        needUpdatePastedRecord: false,

        copiedRecords: undefined as unknown
    }
}

export const workerState = reactive(initialState())

export function reInit(): void {
    Object.assign(workerState, initialState())
}

// MARK: - 부수효과 있는 변경 (구 setter 대체)

/** 월 이동 — 진행 중이던 편집 스냅샷을 함께 비운다 */
export function setSelectedDate(newValue: unknown): void {
    workerState.modifiedRecords = {}
    workerState.oldWorkCodeRecords = undefined
    workerState.selectedDate = newValue
}

export function setModifyMode(isModifyMode: boolean | undefined): void {
    workerState.isModifyMode = isModifyMode
    if (isModifyMode == true) {
        workerState.oldWorkCodeRecords = undefined
        workerState.modifiedRecords = {}
    }
}

export function setNeedUpdatePastedRecord(newValue: boolean): void {
    if (newValue == true) {
        workerState.oldWorkCodeRecords = deepCopy(workerState.modifiedRecords)
    }
    workerState.needUpdatePastedRecord = newValue
}

export function setModifiedRecords(key: string, newValue: unknown): void {
    if (workerState.modifiedRecords == undefined) {
        workerState.modifiedRecords = {}
    }
    workerState.modifiedRecords[key] = newValue
}

// MARK: - vuex 영속 연동 (병원별 필터·근무코드)

export function setShowCustomTimeCode(newValue: boolean): void {
    store.commit('MU_V2WorkCodeFilter_Set', {
        hospitalID: currentHospitalID(),
        isShowCustomTimeCode: newValue,
        isShowBasicEmployee: workerState.isShowBasicEmployee
    })
    workerState.isShowCustomTimeCode = newValue
}

export function setShowBasicEmployee(newValue: boolean): void {
    store.commit('MU_V2WorkCodeFilter_Set', {
        hospitalID: currentHospitalID(),
        isShowCustomTimeCode: workerState.isShowCustomTimeCode,
        isShowBasicEmployee: newValue
    })
    workerState.isShowBasicEmployee = newValue
}

/** 로드된 근무코드에 vuex 에 저장된 선택 상태를 복원해 반영한다 */
export function setWorkCodes(newValue: WorkCode[]): void {
    const origins = storeState.V2WorkCode.hospitals[currentHospitalID()] ?? []
    for (const code of newValue) {
        const stored = origins.find(w => code._id == w._id)
        code.isSelected = stored == undefined ? true : stored.isSelected
    }
    store.commit('MU_V2WorkCode_Set', { hospitalID: currentHospitalID(), codes: newValue })
    workerState.workCodes = newValue
}

export function workCodesUpdate(codes: WorkCode[]): void {
    store.commit('MU_V2WorkCode_Update', { hospitalID: currentHospitalID(), codes: codes })
    for (const code of codes) {
        const origin = workerState.workCodes.find(c => c._id == code._id)
        if (origin == undefined) {
            continue
        }
        origin.isSelected = code.isSelected
    }
}

// MARK: - 레이어·팝업 표시

export function showWorkerSetting(): void { workerState.isShowWorkerSetting = true }
export function closeWorkerSetting(): void { workerState.isShowWorkerSetting = false }

/** 근무코드 설정을 열며 현재 편집 스냅샷을 보관한다 */
export function showWorkCodeSetting(): void {
    workerState.isShowWorkCodeSetting = true
    workerState.oldWorkCodeRecords = workerState.modifiedRecords
}
export function closeWorkCodeSetting(): void { workerState.isShowWorkCodeSetting = false }

export function showCalendarMenu(date: unknown): void { workerState.scheduleMenu_Date = date }
export function hideCalendarMenu(): void { workerState.scheduleMenu_Date = undefined }

export function showCalendarEditLayer(date: unknown): void { workerState.calendarEditLayer_Date = date }
export function hideCalendarEditLayer(): void { workerState.calendarEditLayer_Date = undefined }

export function showCustomTimeOverlay(date: unknown): void { workerState.customTimeOverlay_Date = date }
export function hideCustomTimeOverlay(): void { workerState.customTimeOverlay_Date = undefined }

export function showSearchLayer(key: unknown): void { workerState.searchLayer_Key = key }
export function hideSearchLayer(): void { workerState.searchLayer_Key = undefined }

// MARK: - 복사/붙여넣기

export function setCopiedRecords(records: unknown): void { workerState.copiedRecords = records }
export function clearCopiedRecords(): void { workerState.copiedRecords = undefined }
